import { MatterAssignmentMinDto } from './assignments';
import { AddressDto, ContactMinDto, ContactMinDtoWithAddresses } from './contacts';
import { MatterMin, MatterMinWithClientParty } from './matters';
import { MinDto, MinStringOption, NamedEntityMinDto, NumericIdPrimKeyDto } from './other';

export interface CorrespondenceDetailsDto {
    id: number;
    number: string;
    name: string;
    fullName: string;
    typeCode: CorrespondenceStatusType;
    directionTypeCode: CorrespondenceDirectionType;
    categoryTypeCode: CorrespondenceCategoryType;
    date: string;
    documentDate: string;
    isOfficial: boolean;
    officialReference: string;
    note: string;
    statusTypeCode: string;
    isHandled: boolean;
    dmsLink: string;
    employees: CorrespondenceEmployeeDto[];
    parties: CorrespondencePartyDto[];
    matters: MatterMinWithClientParty[];
    assignments: MatterAssignmentMinDto[];
    tasks: MinDto[];
    createdAt?: string;
    lastModifiedAt?: string;
    createdByEmployee?: MinDto;
    changedByEmployee?: MinDto;
}

export interface CorrespondenceListElementMinDto {
    id: number;
    number: string;
    name: string;
    fullName: string;
    dmsLink?: string;
}

export interface CorrespondenceListElementDto {
    id: number;
    number: string;
    name: string;
    fullName: string;
    typeCode: CorrespondenceStatusType;
    directionTypeCode: CorrespondenceDirectionType;
    date: string;
    documentDate: string;
    isOfficial: boolean;
    officialReference: string;
    note: string;
    statusTypeCode: string;
    isHandled: boolean;
    dmsLink: string;
    employees: CorrespondenceEmployeeDto[];
    parties: CorrespondencePartyDto[];
    matters: MatterMinWithClientParty[];
    tasks: MinDto[];
}

export enum CorrespondenceType {
    EMAIL = 'EMAIL',
}

export enum CorrespondenceStatusType {
    DRAFT = 'DRAFT',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
}

export enum CorrespondenceDirectionType {
    INCOMING = 'INCOMING',
    OUTGOING = 'OUTGOING',
}

enum CorrespondenceCategoryType {
    PRIORITY = 'PRIORITY',
    REGISTERED = 'REGISTERED',
    REGISTERED_PRIORITY = 'REGISTERED_PRIORITY',
    REGISTERED_PRIORITY_WITH_CONFIRMATION_RECEIPT = 'REGISTERED_PRIORITY_WITH_CONFIRMATION_RECEIPT',
    REGISTERED_WITH_CONFIRMATION_RECEIPT = 'REGISTERED_WITH_CONFIRMATION_RECEIPT',
    REGULAR = 'REGULAR',
}

export enum CorrespondencePartyRoleType {
    CLIENT = 'CLIENT',
    RECEIVER = 'RECEIVER',
    SENDER = 'SENDER',
}

export enum CorrespondenceEmployeeRoleType {
    RECEIVER = 'RECEIVER',
}

interface CorrespondenceEmployeeDto {
    employee: MinDto;
    roleTypeCode: CorrespondenceEmployeeRoleType;
}

export type CorrespondencePartyDto =
    | {
          mainParty: ContactMinDtoWithAddresses;
          extraParty: ContactMinDtoWithAddresses | null;
          roleTypeCode: CorrespondencePartyRoleType;
          correspondencePartyAddress: AddressDto | null;
      }
    | {
          mainParty: null;
          extraParty: ContactMinDtoWithAddresses;
          roleTypeCode: CorrespondencePartyRoleType;
          correspondencePartyAddress: AddressDto | null;
      };

export interface CorrespondenceForm {
    readOnlyNumber: string;
    name: string;
    typeCode: MinStringOption | null;
    directionTypeCode: CorrespondenceDirectionType;
    categoryTypeCode: MinStringOption | null;
    date: string | null;
    documentDate: string | null;
    isOfficial: boolean;
    officialReference: string;
    employees: NamedEntityMinDto[];
    sendersOrRecipients: CorrespondenceSenderOrRecipientEntryFormDto[];
    note: string;
    isHandled: boolean;
    statusTypeCode: MinStringOption | null;
    clients: ContactMinDto[];
    matters: MatterMin[];
    assignments: MatterAssignmentMinDto[];
    dmsLink: string;
}

export interface CorrespondenceCreateDto {
    name: string | null;
    typeCode: string | null;
    directionTypeCode: string;
    categoryTypeCode: string | null;
    date: string | null;
    documentDate: string | null;
    isOfficial: boolean;
    officialReference: string | null;
    note: string | null;
    statusTypeCode: string | null;
    isHandled: boolean;
    dmsLink: string | null;
    employees: NumericIdPrimKeyDto[];
    matters: NumericIdPrimKeyDto[];
    assignments: NumericIdPrimKeyDto[];
    sendersOrRecipients: CorrespondenceSenderOrRecipientEntryDto[];
    clients: NumericIdPrimKeyDto[];
}

interface CorrespondenceSenderOrRecipientEntryDto {
    partyPerson: NumericIdPrimKeyDto | null;
    partyCompany: NumericIdPrimKeyDto | null;
    correspondencePartyAddress: AddressDto | null;
}

interface CorrespondenceSenderOrRecipientEntryFormDto {
    // The address fields are a snapshot from selection time and may be absent (some pickers return
    // the bare `ContactMinDto`); the address dropdown subscribes to `getContactById` for the live
    // record and only falls back to this snapshot while that query loads.
    partyPerson: ContactMinDtoWithAddresses | null;
    partyCompany: ContactMinDtoWithAddresses | null;
    correspondencePartyAddress: { label: string; value: AddressDto } | null;
}

export interface CorrespondenceUpdateDto extends CorrespondenceCreateDto {
    id: number | string;
}

export interface CorrespondenceListExportItem {
    id: number;
    number: string;
    name: string;
    typeCode: string;
    categoryTypeCode: string;
    directionTypeCode: CorrespondenceDirectionType;
    date: string;
    documentDate: string;
    isOfficial: boolean;
    officialReference: string;
    note: string;
    statusTypeCode: string;
    isHandled: boolean;
    employees: CorrespondenceEmployeeDto[];
    parties: CorrespondencePartyDto[];
    matters: MatterMinWithClientParty[];
    assignments: MatterAssignmentMinDto[];
}
